import { supabase } from '@/lib/supabase';

export interface BotRealtimeTrade {
  id: string;
  bot_id: string;
  signal_token: string;
  action: string;
  instrument: string;
  timestamp: string;
  win: number;
  amount?: number;
  max_lag?: string;
  investment_type?: string;
  raw_payload?: any;
  created_at: string;
  // Prediction tracking fields
  predicted_result?: 'WIN' | 'LOSS' | 'SKIP';
  prediction_confidence?: number;
  win_probability?: number;
  // AI decision tracking fields
  ai_decision?: 'WIN' | 'LOSS' | 'SKIP';
  ai_confidence?: number;
  // Sequence tracking fields
  prediction_sequence?: string;
  sequence_window_used?: number;
}

export interface BotCurrentPrediction {
  id: string;
  bot_id: string;
  signal_token: string;
  current_prediction: 'WIN' | 'LOSS' | 'SKIP';
  win_probability: number;
  loss_probability: number;
  confidence_score: number;
  sequence_length: number;
  analyzed_sequence?: string;
  last_prediction_at: string;
  prediction_count: number;
  correct_predictions: number;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface BotPatternRefreshLog {
  id: string;
  bot_id: string;
  trading_file_id: string;
  new_trades_count: number;
  total_trades_count: number;
  refresh_triggered_at: string;
  refresh_completed_at?: string;
  refresh_status: 'pending' | 'completed' | 'failed';
  patterns_found?: number;
  error_message?: string;
  processing_time_ms?: number;
  created_at: string;
}

export interface Bot {
  id: string;
  name: string;
  signal_token: string;
  linked_trading_file_id?: string;
  status: string;
  type: string;
  created_date: string;
  last_updated: string;
}

export interface DuplicateTimestamp {
  timestamp: Date;
  duplicateCount: number;
  tradeIds: string[];
  firstTradeId: string;
  sampleAction: string;
  sampleInstrument: string;
  samplePredictedResult?: string;
  sampleWin: boolean;
}

class RealTimeTradingPredictionService {
  /**
   * Get all bots (for linking interface)
   */
  async getAllBots(): Promise<Bot[]> {
    const { data, error } = await supabase
      .from('bots')
      .select(`
        id, name, signal_token, linked_trading_file_id, 
        status, type, created_date, last_updated
      `)
      .order('created_date', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get bots with linked trading files
   */
  async getBotsWithLinkedFiles(): Promise<Bot[]> {
    const { data, error } = await supabase
      .from('bots')
      .select(`
        id, name, signal_token, linked_trading_file_id, 
        status, type, created_date, last_updated
      `)
      .not('linked_trading_file_id', 'is', null);

    if (error) throw error;
    return data || [];
  }

  /**
   * Extract last sequence from CSV data for pattern analysis
   */
  private extractLastSequenceFromCSV(tradingData: number[], maxLength: number = 8): string {
    if (!tradingData || tradingData.length === 0) return '';
    
    const sequenceLength = Math.min(maxLength, tradingData.length);
    const lastSequence = tradingData.slice(-sequenceLength);
    return lastSequence.join(',');
  }

  /**
   * Find similar patterns in historical data
   */
  private findSimilarPatterns(sequence: string, tradingData: number[]): {
    matches: number;
    avgSuccessRate: number;
  } {
    if (!sequence || !tradingData || tradingData.length === 0) {
      return { matches: 0, avgSuccessRate: 0.5 };
    }

    const sequenceArray = sequence.split(',').map(n => parseInt(n));
    const sequenceLength = sequenceArray.length;
    let matches = 0;
    let totalSuccessfulFollows = 0;
    let totalFollows = 0;

    // Find all occurrences of this pattern in historical data
    for (let i = 0; i <= tradingData.length - sequenceLength - 1; i++) {
      const currentPattern = tradingData.slice(i, i + sequenceLength);
      
      // Check if pattern matches
      if (JSON.stringify(currentPattern) === JSON.stringify(sequenceArray)) {
        matches++;
        const nextResult = tradingData[i + sequenceLength];
        if (nextResult !== undefined) {
          totalFollows++;
          if (nextResult === 1) totalSuccessfulFollows++;
        }
      }
    }

    const avgSuccessRate = totalFollows > 0 ? totalSuccessfulFollows / totalFollows : 0.5;
    return { matches, avgSuccessRate };
  }

  /**
   * Generate prediction from pattern analysis
   */
  private generatePredictionFromPattern(
    sequence: string, 
    tradingData: number[]
  ): {
    prediction: 'WIN' | 'LOSS' | 'SKIP';
    winProbability: number;
    lossProbability: number;
    confidence: number;
  } {
    if (!sequence || !tradingData) {
      return {
        prediction: 'SKIP',
        winProbability: 0.5,
        lossProbability: 0.5,
        confidence: 0.0
      };
    }

    const { matches, avgSuccessRate } = this.findSimilarPatterns(sequence, tradingData);
    
    // Calculate confidence based on pattern frequency and consistency
    let confidence = 0.0;
    if (matches >= 10) confidence = 0.9;
    else if (matches >= 5) confidence = 0.75;
    else if (matches >= 3) confidence = 0.6;
    else if (matches >= 2) confidence = 0.45;
    else confidence = 0.2;

    // Adjust confidence based on success rate consistency
    const successRateConfidence = Math.abs(avgSuccessRate - 0.5) * 2;
    confidence = Math.min(confidence * (0.5 + successRateConfidence), 1.0);

    const winProbability = avgSuccessRate;
    const lossProbability = 1 - avgSuccessRate;

    // Generate prediction based on pattern analysis
    let prediction: 'WIN' | 'LOSS' | 'SKIP' = 'SKIP';
    
    if (confidence >= 0.6) {
      // High confidence - make prediction
      prediction = winProbability > 0.55 ? 'WIN' : 'LOSS';
    } else if (confidence >= 0.4 && (winProbability > 0.65 || winProbability < 0.35)) {
      // Medium confidence with strong bias
      prediction = winProbability > 0.5 ? 'WIN' : 'LOSS';
    }
    // Otherwise keep SKIP for low confidence

    return {
      prediction,
      winProbability,
      lossProbability,
      confidence
    };
  }

  /**
   * Link a bot to a trading file
   */
  async linkBotToTradingFile(botId: string, tradingFileId: string): Promise<void> {
    console.log(`🔗 Starting link process for bot ${botId} to file ${tradingFileId}`);
    
    try {
      // Start transaction - get bot details
      const { data: bot, error: botError } = await supabase
        .from('bots')
        .select('signal_token, max_sequence_length, owner_id, linked_trading_file_id')
        .eq('id', botId)
        .single();

      if (botError) {
        console.error('❌ Failed to fetch bot details:', botError);
        throw new Error(`Failed to fetch bot details: ${botError.message}`);
      }

      if (!bot) {
        throw new Error(`Bot with ID ${botId} not found`);
      }

      console.log(`📋 Bot details:`, {
        id: botId,
        signal_token: bot.signal_token,
        owner_id: bot.owner_id,
        current_linked_file: bot.linked_trading_file_id
      });

      // Fetch trading data from CSV file to calculate real values
      const { data: tradingFile, error: fileError } = await supabase
        .from('trading_files')
        .select('trading_data')
        .eq('id', tradingFileId)
        .single();

      if (fileError) throw fileError;

      // Extract and analyze pattern from CSV data
      let sequenceLength = 0;
      let analyzedSequence = '';
      let currentPrediction: 'WIN' | 'LOSS' | 'SKIP' = 'SKIP';
      let winProbability = 0.5;
      let lossProbability = 0.5;
      let confidence = 0.0;
      let accuracyRate = 0.0;

      if (tradingFile?.trading_data && Array.isArray(tradingFile.trading_data)) {
        const tradingData = tradingFile.trading_data;
        sequenceLength = tradingData.length;
        
        // Extract last sequence for pattern analysis
        const maxSequenceLength = bot.max_sequence_length || 8;
        analyzedSequence = this.extractLastSequenceFromCSV(tradingData, maxSequenceLength);
        
        if (analyzedSequence) {
          try {
            // Use same Edge Function logic as Manual Prediction for consistent calculation
            const { TradingAnalysisOrchestrator } = await import('@/services/trading-analysis/TradingAnalysisOrchestrator');
            const prediction = await TradingAnalysisOrchestrator.predictNextTrade(
              tradingFileId,
              analyzedSequence,
              'basic' // Use Basic mode for consistent calculation
            );
            
            currentPrediction = prediction.winProbability > 0.55 ? 'WIN' : 
                               prediction.winProbability < 0.45 ? 'LOSS' : 'SKIP';
            winProbability = prediction.winProbability;
            lossProbability = prediction.lossProbability;
            
            // Convert confidence level string to number
            const confidenceLevelMap = {
              'very_low': 0.2,
              'low': 0.4,
              'medium': 0.6,
              'high': 0.8,
              'very_high': 0.9
            };
            confidence = confidenceLevelMap[prediction.confidenceLevel] || 0.5;
            
            // Calculate accuracy rate based on confidence level
            accuracyRate = Math.min(confidence * 100, 85); // Cap at 85% for initial
            
            console.log(`📊 Edge Function Pattern Analysis - Sequence: ${analyzedSequence}, Prediction: ${currentPrediction}, Win: ${(winProbability * 100).toFixed(1)}%, Loss: ${(lossProbability * 100).toFixed(1)}%, Confidence: ${(confidence * 100).toFixed(1)}%`);
          } catch (edgeFunctionError) {
            console.warn('⚠️ Edge Function prediction failed, falling back to local method:', edgeFunctionError);
            
            // Fallback to local method if Edge Function fails
            const prediction = this.generatePredictionFromPattern(analyzedSequence, tradingData);
            currentPrediction = prediction.prediction;
            winProbability = prediction.winProbability;
            lossProbability = prediction.lossProbability;
            confidence = prediction.confidence;
            
            const { matches } = this.findSimilarPatterns(analyzedSequence, tradingData);
            if (matches >= 3) {
              accuracyRate = Math.min(confidence * 100, 85);
            }
          }
        }
      } else {
        console.warn('⚠️ No trading_data found in CSV file, using default values');
      }

      // Update bot with linked file
      console.log(`🔄 Updating bot ${botId} with linked_trading_file_id: ${tradingFileId}`);
      
      const { data: updateData, error: updateError } = await supabase
        .from('bots')
        .update({ 
          linked_trading_file_id: tradingFileId,
          last_updated: new Date().toISOString()
        })
        .eq('id', botId)
        .select('linked_trading_file_id');

      if (updateError) {
        console.error('❌ Failed to update bot linked_trading_file_id:', updateError);
        
        // Enhanced error handling for RLS policy issues
        if (updateError.message?.includes('permission denied') || 
            updateError.message?.includes('policy')) {
          throw new Error('Permission denied: Admin RLS policy may not be active. Please apply the RLS policy fix.');
        }
        
        throw new Error(`Failed to update bot: ${updateError.message}`);
      }

      if (!updateData || updateData.length === 0) {
        console.error('❌ No rows updated - possible RLS policy issue');
        console.error('Bot owner_id:', bot.owner_id);
        console.error('Current user auth state - check if admin role is properly set');
        throw new Error('Failed to update bot - no rows affected. Admin RLS policy may not be active.');
      }

      console.log(`✅ Successfully updated bot ${botId} with linked_trading_file_id:`, updateData[0].linked_trading_file_id);

      // Validate and clamp probability values to prevent numeric overflow
      // Ensure probabilities are in 0-1 range before multiplying by 100
      const validatedWinProbability = Math.max(0, Math.min(1, winProbability));
      const validatedLossProbability = Math.max(0, Math.min(1, lossProbability));
      
      // Calculate final values for database (0-100 range, max 999.9999 due to NUMERIC(7,4))
      const dbWinProbability = Math.min(999.9999, validatedWinProbability * 100);
      const dbLossProbability = Math.min(999.9999, validatedLossProbability * 100);
      
      console.log(`📊 Probability values - Original: Win=${winProbability}, Loss=${lossProbability} | Validated: Win=${validatedWinProbability}, Loss=${validatedLossProbability} | DB: Win=${dbWinProbability}, Loss=${dbLossProbability}`);

      // Create initial prediction record with real pattern analysis
      const { error: predictionError } = await supabase
        .from('bot_current_prediction')
        .insert({
          bot_id: botId,
          signal_token: bot.signal_token,
          current_prediction: currentPrediction,
          win_probability: dbWinProbability,
          loss_probability: dbLossProbability,
          confidence_score: confidence,
          sequence_length: sequenceLength,
          analyzed_sequence: analyzedSequence,
          last_prediction_at: new Date().toISOString(),
          prediction_count: currentPrediction !== 'SKIP' ? 1 : 0,
          correct_predictions: 0,
          metadata: {
            status: 'linked',
            created_by: 'pattern_analysis',
            linked_file_id: tradingFileId,
            csv_sequence_length: sequenceLength,
            initial_pattern: analyzedSequence,
            initial_prediction: currentPrediction
          }
        });

      if (predictionError) throw predictionError;

      console.log(`✅ Bot ${botId} linked with pattern analysis: ${currentPrediction} prediction (${(confidence * 100).toFixed(1)}% confidence)`);
      
      // Verify the link was successful by checking the database
      const { data: verifyData, error: verifyError } = await supabase
        .from('bots')
        .select('linked_trading_file_id')
        .eq('id', botId)
        .single();
        
      if (verifyError || !verifyData?.linked_trading_file_id) {
        console.error('❌ Verification failed - bot not properly linked:', verifyError);
        throw new Error('Link verification failed - bot may not be properly linked');
      }
      
      console.log(`🔍 Link verification successful: Bot ${botId} is linked to file ${verifyData.linked_trading_file_id}`);
      
    } catch (error) {
      console.error('❌ Failed to link bot:', error);
      
      // Enhanced error reporting
      if (error instanceof Error) {
        if (error.message.includes('RLS')) {
          throw new Error('Permission denied: Unable to update bot. Please check RLS policies.');
        } else if (error.message.includes('no rows affected')) {
          throw new Error('Update failed: No bot was updated. This may be due to permission issues.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Unlink a bot from trading file - SAFELY delete all real-time prediction data
   */
  async unlinkBotFromTradingFile(botId: string): Promise<void> {
    try {
      console.log(`🔗 Starting unlink process for bot ${botId}`);

      // 1. Update bot to remove linked file (keep bot record!)
      const { error: updateError } = await supabase
        .from('bots')
        .update({ linked_trading_file_id: null })
        .eq('id', botId);

      if (updateError) throw updateError;

      // 2. Delete ALL real-time prediction data (SAFE - no bot record deletion)
      const deleteOperations = [
        // Delete current prediction
        supabase
          .from('bot_current_prediction')
          .delete()
          .eq('bot_id', botId),
        
        // Delete realtime trades
        supabase
          .from('bot_realtime_trades')
          .delete()
          .eq('bot_id', botId),
        
        // Delete pattern refresh logs
        supabase
          .from('bot_pattern_refresh_log')
          .delete()
          .eq('bot_id', botId),
        
        // Delete signal processing history (ENTER & EXIT signals)
        supabase
          .from('signal_processing_history')
          .delete()
          .eq('bot_id', botId),
        
        // Delete pattern update history
        supabase
          .from('pattern_incremental_updates')
          .delete()
          .eq('bot_id', botId)
      ];

      // Execute all delete operations
      const results = await Promise.allSettled(deleteOperations);
      
      // Log results
      results.forEach((result, index) => {
        const tables = ['bot_current_prediction', 'bot_realtime_trades', 'bot_pattern_refresh_log', 'signal_processing_history', 'pattern_incremental_updates'];
        if (result.status === 'fulfilled') {
          console.log(`✅ Deleted from ${tables[index]}`);
        } else {
          console.warn(`⚠️ Error deleting from ${tables[index]}:`, result.reason);
        }
      });

      console.log(`✅ Bot ${botId} unlinked and all real-time prediction data cleaned up`);
    } catch (error) {
      console.error('❌ Failed to unlink bot:', error);
      throw error;
    }
  }

  /**
   * Get realtime trades for a bot
   */
  async getBotRealtimeTrades(
    botId: string, 
    limit: number = 50,
    offset: number = 0
  ): Promise<{ trades: BotRealtimeTrade[], total: number }> {
    const { data: trades, error } = await supabase
      .from('bot_realtime_trades')
      .select('*')
      .eq('bot_id', botId)
      .order('timestamp', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    const { count } = await supabase
      .from('bot_realtime_trades')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId);

    return {
      trades: trades || [],
      total: count || 0
    };
  }

  /**
   * Clear all real-time trades for a bot (keeping the bot linked to CSV)
   */
  async clearBotRealTimeTrades(botId: string): Promise<void> {
    console.log(`🗑️ Starting clear real-time trades for bot ${botId}`);
    
    try {
      // Call edge function with service role permissions to delete trades
      const { data, error } = await supabase.functions.invoke('clear-bot-trades', {
        body: { botId }
      });

      if (error) {
        console.error('❌ Edge function error:', error);
        throw new Error(`Failed to clear trades: ${error.message}`);
      }

      if (!data?.success) {
        console.error('❌ Clear trades failed:', data);
        throw new Error(data?.error || 'Failed to clear trades');
      }

      console.log(`✅ Successfully cleared ${data.deletedCount || 0} real-time trades for bot ${data.botName} (${botId})`);
    } catch (error) {
      console.error('❌ Failed to clear real-time trades:', error);
      throw error;
    }
  }

  /**
   * Get current prediction for a bot
   */
  async getBotCurrentPrediction(botId: string): Promise<BotCurrentPrediction | null> {
    const { data, error } = await supabase
      .from('bot_current_prediction')
      .select('*')
      .eq('bot_id', botId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  /**
   * Get current predictions for multiple bots
   */
  async getBotsCurrentPredictions(botIds: string[]): Promise<BotCurrentPrediction[]> {
    const { data, error } = await supabase
      .from('bot_current_prediction')
      .select('*')
      .in('bot_id', botIds);

    if (error) throw error;
    return data || [];
  }

  /**
   * Update prediction accuracy when trade result is known
   */
  async updatePredictionAccuracy(
    botId: string, 
    actualResult: number, 
    predictedResult: 'WIN' | 'LOSS'
  ): Promise<void> {
    const isCorrect = (predictedResult === 'WIN' && actualResult === 1) || 
                     (predictedResult === 'LOSS' && actualResult === 0);

    // Get current prediction to calculate new accuracy
    const currentPrediction = await this.getBotCurrentPrediction(botId);
    if (!currentPrediction) return;

    const newCorrectPredictions = currentPrediction.correct_predictions + (isCorrect ? 1 : 0);
    
    const { error } = await supabase
      .from('bot_current_prediction')
      .update({
        correct_predictions: newCorrectPredictions,
        updated_at: new Date().toISOString()
      })
      .eq('bot_id', botId);

    if (error) throw error;
  }

  /**
   * Get pattern refresh logs for a bot
   */
  async getBotPatternRefreshLogs(
    botId: string, 
    limit: number = 10
  ): Promise<BotPatternRefreshLog[]> {
    const { data, error } = await supabase
      .from('bot_pattern_refresh_log')
      .select('*')
      .eq('bot_id', botId)
      .order('refresh_triggered_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  /**
   * Check if pattern refresh is needed for a bot
   */
  async checkRefreshNeeded(botId: string, threshold: number = 20): Promise<boolean> {
    // Get last refresh timestamp
    const { data: lastRefresh } = await supabase
      .from('bot_pattern_refresh_log')
      .select('refresh_triggered_at, total_trades_count')
      .eq('bot_id', botId)
      .eq('refresh_status', 'completed')
      .order('refresh_triggered_at', { ascending: false })
      .limit(1)
      .single();

    // Count trades since last refresh
    const { count: tradesCount } = await supabase
      .from('bot_realtime_trades')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .gte('created_at', lastRefresh?.refresh_triggered_at || '1900-01-01');

    const newTradesCount = tradesCount || 0;
    return newTradesCount >= threshold;
  }

  /**
   * Auto-trigger pattern refresh if threshold is met
   */
  async autoTriggerRefreshIfNeeded(botId: string, tradingFileId: string): Promise<string | null> {
    const refreshNeeded = await this.checkRefreshNeeded(botId);
    
    if (refreshNeeded) {
      console.log(`🔄 Auto-triggering pattern refresh for bot ${botId}`);
      return await this.triggerPatternRefresh(botId, tradingFileId);
    }
    
    return null;
  }

  /**
   * Trigger pattern refresh for a bot
   */
  async triggerPatternRefresh(botId: string, tradingFileId: string, dryRun: boolean = false): Promise<string> {
    try {
      console.log(`🔄 Triggering pattern refresh for bot: ${botId}${dryRun ? ' (DRY RUN)' : ''}`);
      
      // Call the new pattern-refresh edge function
      const { data, error } = await supabase.functions.invoke('pattern-refresh', {
        body: {
          botId: botId,
          tradingFileId: tradingFileId,
          dryRun: dryRun
        }
      });

      if (error) {
        console.error('❌ Error triggering pattern refresh:', error);
        throw error;
      }

      console.log('✅ Pattern refresh completed:', data);
      return data?.refreshId || 'refresh-' + Date.now();
    } catch (error) {
      console.error('❌ Failed to trigger pattern refresh:', error);
      throw error;
    }
  }

  /**
   * Build combined trading sequence (CSV historical + realtime trades)
   */
  private async buildCombinedTradingSequence(botId: string, tradingFileId: string): Promise<number[]> {
    // Get CSV historical data
    const { data: tradingFile } = await supabase
      .from('trading_files')
      .select('trading_data')
      .eq('id', tradingFileId)
      .single();

    let sequence: number[] = [];
    
    if (tradingFile?.trading_data) {
      sequence = [...tradingFile.trading_data];
    }

    // Append realtime trades
    const { data: realtimeTrades } = await supabase
      .from('bot_realtime_trades')
      .select('win')
      .eq('bot_id', botId)
      .order('timestamp', { ascending: true });

    if (realtimeTrades) {
      const realtimeResults = realtimeTrades.map((trade: any) => trade.win);
      sequence.push(...realtimeResults);
    }

    return sequence;
  }

  /**
   * Get bot trading statistics
   */
  async getBotTradingStats(botId: string): Promise<{
    totalTrades: number;
    winRate: number;
    predictionAccuracy: number;
    recentPredictions: Array<{
      prediction: string;
      timestamp: string;
      confidence: number;
    }>;
  }> {
    // Get realtime trades
    const { data: trades } = await supabase
      .from('bot_realtime_trades')
      .select('win, timestamp')
      .eq('bot_id', botId)
      .order('timestamp', { ascending: false });

    // Get current prediction
    const prediction = await this.getBotCurrentPrediction(botId);

    const totalTrades = trades?.length || 0;
    const wins = trades?.filter(t => t.win === 1).length || 0;
    const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;

    const recentPredictions = prediction ? [{
      prediction: prediction.current_prediction,
      timestamp: prediction.last_prediction_at,
      confidence: prediction.confidence_score * 100
    }] : [];

    return {
      totalTrades,
      winRate,
      predictionAccuracy: prediction && prediction.prediction_count > 0 
        ? (prediction.correct_predictions / prediction.prediction_count) * 100 
        : 0,
      recentPredictions
    };
  }

  /**
   * Get duplicate timestamps for a bot within optional date range
   */
  async getDuplicateTimestamps(
    botId: string, 
    fromDate?: Date, 
    toDate?: Date
  ): Promise<DuplicateTimestamp[]> {
    const { data, error } = await supabase.rpc('find_bot_trade_duplicates', {
      p_bot_id: botId,
      p_from_ts: fromDate?.toISOString(),
      p_to_ts: toDate?.toISOString()
    });

    if (error) {
      console.error('Error fetching duplicate timestamps:', error);
      throw error;
    }

    return data?.map((row: any) => ({
      timestamp: new Date(row.trade_timestamp),
      duplicateCount: row.duplicate_count,
      tradeIds: row.trade_ids,
      firstTradeId: row.first_trade_id,
      sampleAction: row.sample_action,
      sampleInstrument: row.sample_instrument,
      samplePredictedResult: row.sample_predicted_result,
      sampleWin: row.sample_win
    })) || [];
  }

  /**
   * Get real-time prediction stream (mock implementation for now)
   */
  subscribeToRealTimePredictions(
    botId: string, 
    callback: (prediction: BotCurrentPrediction) => void
  ): () => void {
    // Subscribe to real-time changes
    const subscription = supabase
      .channel(`bot_predictions_${botId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bot_current_prediction',
          filter: `bot_id=eq.${botId}`
        },
        (payload) => {
          if (payload.new) {
            callback(payload.new as BotCurrentPrediction);
          }
        }
      )
      .subscribe();

    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
    };
  }
}

export const realTimeTradingPredictionService = new RealTimeTradingPredictionService();